// Hand-rolled inline SVG -- deliberately not a chart library, matching
// burndown-chart.tsx / gantt-chart.tsx. One stacked horizontal bar per stage
// showing how its tasks are distributed across the state machine, plus an
// overall bar. Delivery slice 7 of docs/plans/2026-08-pm-onboarding-lms.md.

type TaskState = "locked" | "open" | "submitted" | "approved" | "rejected";

const ORDER: TaskState[] = ["approved", "submitted", "rejected", "open", "locked"];
const FILL: Record<TaskState, string> = {
  approved: "var(--bs-success, #198754)",
  submitted: "var(--bs-warning, #ffc107)",
  rejected: "var(--bs-danger, #dc3545)",
  open: "var(--bs-primary, #0d6efd)",
  locked: "var(--bs-secondary-bg, #6c757d)",
};
const LABEL: Record<TaskState, string> = {
  approved: "Approved", submitted: "Awaiting review", rejected: "Changes requested", open: "To do", locked: "Locked",
};

type Row = { label: string; counts: Record<TaskState, number>; total: number };

const ROW_HEIGHT = 26;
const GAP = 6;
const LABEL_WIDTH = 128;
const BAR_WIDTH = 320;
const COUNT_WIDTH = 46; // room for the trailing "approved/total" label
const WIDTH = LABEL_WIDTH + BAR_WIDTH + COUNT_WIDTH;

export function OnboardingProgress({ stages }: {
  stages: Array<{ title: string; tasks: Array<{ state: TaskState }> }>;
}) {
  const rows: Row[] = stages
    .filter((stage) => stage.tasks.length > 0)
    .map((stage) => {
      const counts = { approved: 0, submitted: 0, rejected: 0, open: 0, locked: 0 } as Record<TaskState, number>;
      for (const task of stage.tasks) counts[task.state] += 1;
      return { counts, label: stage.title, total: stage.tasks.length };
    });
  if (!rows.length) return null;

  const overall = rows.reduce(
    (accumulator, row) => {
      for (const state of ORDER) accumulator.counts[state] += row.counts[state];
      accumulator.total += row.total;
      return accumulator;
    },
    { counts: { approved: 0, submitted: 0, rejected: 0, open: 0, locked: 0 } as Record<TaskState, number>, label: "All stages", total: 0 },
  );
  const allRows = [overall, ...rows];
  const height = allRows.length * (ROW_HEIGHT + GAP) + 24;
  const usedStates = ORDER.filter((state) => overall.counts[state] > 0);

  return (
    <figure className="mb-3">
      <svg viewBox={`0 0 ${WIDTH} ${height}`} width="100%" style={{ maxWidth: WIDTH }} role="img"
        aria-label={`Onboarding progress: ${overall.counts.approved} of ${overall.total} tasks approved`}>
        {allRows.map((row, index) => {
          const y = index * (ROW_HEIGHT + GAP);
          let x = LABEL_WIDTH;
          return (
            <g key={row.label}>
              <text x={LABEL_WIDTH - 6} y={y + ROW_HEIGHT / 2} textAnchor="end" dominantBaseline="central"
                fontSize="11" fill="currentColor" fontWeight={index === 0 ? 700 : 400}>
                {row.label.length > 20 ? `${row.label.slice(0, 19)}…` : row.label}
              </text>
              {row.total === 0
                ? <rect x={LABEL_WIDTH} y={y} width={BAR_WIDTH} height={ROW_HEIGHT} fill="var(--bs-secondary-bg, #e9ecef)" rx="3" />
                : ORDER.map((state) => {
                    const segment = (row.counts[state] / row.total) * BAR_WIDTH;
                    if (segment <= 0) return null;
                    const rect = <rect key={state} x={x} y={y} width={segment} height={ROW_HEIGHT} fill={FILL[state]}><title>{`${row.label}: ${row.counts[state]} ${LABEL[state].toLowerCase()}`}</title></rect>;
                    x += segment;
                    return rect;
                  })}
              <text x={LABEL_WIDTH + BAR_WIDTH + 4} y={y + ROW_HEIGHT / 2} dominantBaseline="central" fontSize="10" fill="currentColor">
                {row.counts.approved}/{row.total}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="d-flex flex-wrap gap-3 small text-body-secondary mt-1">
        {usedStates.map((state) => (
          <span key={state} className="d-inline-flex align-items-center gap-1">
            <span style={{ background: FILL[state], borderRadius: 2, display: "inline-block", height: 10, width: 10 }} />
            {LABEL[state]}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
